// apps/web/src/pages/ImportHistoryPage.tsx
// AI 导题生成记录页：列出所有导入任务

import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { aiImportListJobsApi, aiImportDeleteJobApi } from "@/api/aiImport";
import type { AIImportJob } from "@/api/aiImport";
import ImportProgress, { getProgressMeta } from "@/components/aiImport/ImportProgress";

export default function ImportHistoryPage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<AIImportJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const jobsRef = useRef(jobs);

  // 保持 ref 与最新 state 同步，供轮询使用
  jobsRef.current = jobs;

  const fetchJobs = async (silent = false) => {
    try {
      if (!silent) setIsLoading(true);
      const res = await aiImportListJobsApi(1, 100);
      setJobs(res.items);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "加载失败");
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    // 如有处理中任务，自动轮询刷新进度
    const timer = setInterval(() => {
      const hasProcessing = jobsRef.current.some((j) => j.status === "processing");
      if (hasProcessing) {
        fetchJobs(true);
      }
    }, 2500);
    return () => clearInterval(timer);
  }, []);

  const handleDelete = async (jobId: string) => {
    if (!confirm("确定删除这条导入记录？已生成的题库不会被删除。")) return;
    try {
      await aiImportDeleteJobApi(jobId);
      setJobs((prev) => prev.filter((j) => j.jobId !== jobId));
      toast.success("已删除");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "删除失败");
    }
  };

  const formatSize = (bytes?: number) => {
    if (!bytes) return "";
    return `${(bytes / 1024).toFixed(2)}KB`;
  };

  const formatDate = (ts?: number) => {
    if (!ts) return "";
    const d = new Date(ts);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const formatFailureLog = (logs?: string | null) => {
    if (!logs) return "";
    const diagnostics = logs
      .split("\n")
      .filter((line) => line.includes("[diagnostic]"))
      .join("\n")
      .trim();
    return (diagnostics || logs.trim()).slice(-6000);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-6">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="text-gray-700 text-sm flex items-center gap-1">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h1 className="text-base font-semibold text-gray-900">生成记录</h1>
        <div className="w-5" />
      </div>

      <div className="p-4 space-y-4">
        {isLoading ? (
          <div className="text-center py-12 text-sm text-gray-400">加载中...</div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
              <FileIcon />
            </div>
            <p className="text-sm text-gray-500 mb-4">暂无导入记录</p>
            <Link
              to="/import"
              className="inline-block px-5 py-2 bg-primary text-white rounded-full text-sm font-medium"
            >
              去导入
            </Link>
          </div>
        ) : (
          jobs.map((job) => (
            <div key={job.jobId} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-start gap-3">
                {/* 文件图标 */}
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                  <FileTypeIcon type={job.fileType} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-medium text-gray-900 truncate">{job.fileName}</h3>
                    <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 text-[10px] rounded">AI导题</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {formatDate(job.createdAt)} · {formatSize(job.fileSize)}
                  </p>

                  {job.status === "processing" && (
                    <div className="mt-3">
                      <ImportProgress progress={job.progress} logs={job.logs} />
                    </div>
                  )}

                  {job.status === "completed" && (
                    <>
                      <span className="inline-block mt-3 text-xs px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700">
                        {getProgressMeta(job.progress).label}
                      </span>
                      <p className="text-sm text-gray-600 mt-2">
                        {job.successCount}道试题已导入
                        {job.bankName ? (
                          <>
                            【
                            <Link
                              to={`/banks/${job.bankId}`}
                              className="text-blue-600 hover:underline"
                            >
                              {job.bankName}
                            </Link>
                            】
                          </>
                        ) : null}
                        中，若需调整，请前往“我的学习空间”点击“更多-编辑试题”
                      </p>
                    </>
                  )}

                  {job.status === "failed" && (
                    <>
                      <span className="inline-block mt-3 text-xs px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700">
                        失败
                      </span>
                      <p className="text-sm text-red-500 mt-2">{job.error ?? "导入失败"}</p>
                      {formatFailureLog(job.logs) ? (
                        <div className="mt-2 rounded-lg bg-red-50 border border-red-100 p-2.5">
                          <p className="text-xs font-medium text-red-700 mb-2">失败诊断日志</p>
                          <pre className="text-[11px] font-mono leading-relaxed text-red-700 whitespace-pre-wrap break-words max-h-80 overflow-y-auto">
                            {formatFailureLog(job.logs)}
                          </pre>
                        </div>
                      ) : null}
                    </>
                  )}

                  <div className="mt-3 flex items-center gap-4">
                    {job.bankId && job.status === "completed" && (
                      <Link
                        to={`/banks/${job.bankId}`}
                        className="text-sm text-blue-600 font-medium hover:underline"
                      >
                        查看详情
                      </Link>
                    )}
                    <button
                      onClick={() => handleDelete(job.jobId)}
                      className="text-sm text-gray-400 hover:text-danger"
                    >
                      删除
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}

        {!isLoading && jobs.length > 0 && (
          <p className="text-center text-xs text-gray-400 mt-6">保留近1年上传记录</p>
        )}
      </div>
    </div>
  );
}

function FileIcon() {
  return (
    <svg className="w-7 h-7 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}

function FileTypeIcon({ type }: { type: string | null }) {
  const color = type === "Word" ? "#3B82F6" : type === "Excel" ? "#10B981" : type === "PDF" ? "#EF4444" : "#6B7280";
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
      <rect x="4" y="3" width="16" height="18" rx="2" fill={color} opacity="0.15" />
      <path d="M7 7h10v2H7V7zm0 4h10v2H7v-2zm0 4h7v2H7v-2z" fill={color} />
    </svg>
  );
}
